import { useState, useEffect } from 'react'

interface PricingInfo {
  price: number
  regularPrice: number
  symbol: string
  locale: string
  currencyCode: string
  formattedPrice: string
  formattedRegularPrice: string
}

interface RegionState extends PricingInfo {
  loading: boolean
  error: boolean
}

const FALLBACK_PRICING = {
  price: 16.99,
  regularPrice: 49.99,
  symbol: '$',
  locale: 'en-US',
  currencyCode: 'USD',
}

const PRICING_MAP: Record<
  string,
  Omit<PricingInfo, 'formattedPrice' | 'formattedRegularPrice'>
> = {
  US: {
    price: 16.99,
    regularPrice: 49.99,
    symbol: '$',
    locale: 'en-US',
    currencyCode: 'USD',
  },
  ES: {
    price: 14.99,
    regularPrice: 42.99,
    symbol: '€',
    locale: 'es-ES',
    currencyCode: 'EUR',
  },
  CL: {
    price: 14999,
    regularPrice: 43999,
    symbol: '$',
    locale: 'es-CL',
    currencyCode: 'CLP',
  },
  AR: {
    price: 23999,
    regularPrice: 70999,
    symbol: '$',
    locale: 'es-AR',
    currencyCode: 'ARS',
  },
  CO: {
    price: 63999,
    regularPrice: 189999,
    symbol: '$',
    locale: 'es-CO',
    currencyCode: 'COP',
  },
  MX: {
    price: 299,
    regularPrice: 899,
    symbol: '$',
    locale: 'es-MX',
    currencyCode: 'MXN',
  },
  UY: {
    price: 670,
    regularPrice: 1999,
    symbol: '$',
    locale: 'es-UY',
    currencyCode: 'UYU',
  },
}

// Timezone → country code mapping for fallback detection
const TIMEZONE_TO_COUNTRY: Record<string, string> = {
  'America/Argentina/Buenos_Aires': 'AR',
  'America/Argentina/Cordoba': 'AR',
  'America/Argentina/Salta': 'AR',
  'America/Argentina/Tucuman': 'AR',
  'America/Argentina/Jujuy': 'AR',
  'America/Argentina/Catamarca': 'AR',
  'America/Argentina/La_Rioja': 'AR',
  'America/Argentina/San_Juan': 'AR',
  'America/Argentina/Mendoza': 'AR',
  'America/Argentina/San_Luis': 'AR',
  'America/Argentina/Rio_Gallegos': 'AR',
  'America/Argentina/Ushuaia': 'AR',
  'America/Santiago': 'CL',
  'America/Punta_Arenas': 'CL',
  'Pacific/Easter': 'CL',
  'America/Bogota': 'CO',
  'America/Mexico_City': 'MX',
  'America/Cancun': 'MX',
  'America/Merida': 'MX',
  'America/Monterrey': 'MX',
  'America/Matamoros': 'MX',
  'America/Chihuahua': 'MX',
  'America/Ojinaga': 'MX',
  'America/Mazatlan': 'MX',
  'America/Bahia_Banderas': 'MX',
  'America/Hermosillo': 'MX',
  'America/Tijuana': 'MX',
  'America/Montevideo': 'UY',
  'Europe/Madrid': 'ES',
  'Atlantic/Canary': 'ES',
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Los_Angeles': 'US',
  'America/Anchorage': 'US',
  'Pacific/Honolulu': 'US',
  'America/Phoenix': 'US',
  'America/Boise': 'US',
  'America/Indiana/Indianapolis': 'US',
  'America/Detroit': 'US',
}

function getCountryFromTimezone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    return TIMEZONE_TO_COUNTRY[tz] || null
  } catch {
    return null
  }
}

const formatPrice = (amount: number, locale: string, currency: string) => {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount)
  } catch (e) {
    return `${amount} ${currency}`
  }
}

// Try multiple geo APIs in sequence
async function detectCountry(): Promise<string | null> {
  // Attempt 1: ipapi.co
  try {
    const controller1 = new AbortController()
    const timeout1 = setTimeout(() => controller1.abort(), 4000)
    const res1 = await fetch('https://ipapi.co/json/', {
      signal: controller1.signal,
    })
    clearTimeout(timeout1)
    if (res1.ok) {
      const data1 = await res1.json()
      if (data1.country_code) return data1.country_code
    }
  } catch {
    // continue to next
  }

  // Attempt 2: ipwho.is
  try {
    const controller2 = new AbortController()
    const timeout2 = setTimeout(() => controller2.abort(), 4000)
    const res2 = await fetch('https://ipwho.is/', {
      signal: controller2.signal,
    })
    clearTimeout(timeout2)
    if (res2.ok) {
      const data2 = await res2.json()
      if (data2.success !== false && data2.country_code)
        return data2.country_code
    }
  } catch {
    // continue to next
  }

  // Attempt 3: ipinfo.io (no token, limited)
  try {
    const controller3 = new AbortController()
    const timeout3 = setTimeout(() => controller3.abort(), 4000)
    const res3 = await fetch('https://ipinfo.io/json', {
      signal: controller3.signal,
    })
    clearTimeout(timeout3)
    if (res3.ok) {
      const data3 = await res3.json()
      if (data3.country) return data3.country
    }
  } catch {
    // continue to fallback
  }

  // Attempt 4: Timezone-based detection
  return getCountryFromTimezone()
}

export function useUserRegion(): RegionState {
  const [state, setState] = useState<RegionState>(() => {
    // On initial render, try timezone immediately for a fast first paint
    const tzCountry = getCountryFromTimezone()
    const initialPricing =
      (tzCountry && PRICING_MAP[tzCountry]) || FALLBACK_PRICING

    return {
      ...initialPricing,
      formattedPrice: formatPrice(
        initialPricing.price,
        initialPricing.locale,
        initialPricing.currencyCode,
      ),
      formattedRegularPrice: formatPrice(
        initialPricing.regularPrice,
        initialPricing.locale,
        initialPricing.currencyCode,
      ),
      loading: true,
      error: false,
    }
  })

  useEffect(() => {
    let mounted = true

    const fetchRegion = async () => {
      try {
        const countryCode = await detectCountry()

        if (mounted) {
          const pricing =
            (countryCode && PRICING_MAP[countryCode]) || FALLBACK_PRICING

          setState({
            ...pricing,
            formattedPrice: formatPrice(
              pricing.price,
              pricing.locale,
              pricing.currencyCode,
            ),
            formattedRegularPrice: formatPrice(
              pricing.regularPrice,
              pricing.locale,
              pricing.currencyCode,
            ),
            loading: false,
            error: false,
          })
        }
      } catch (error) {
        console.error('Error detecting region:', error)
        if (mounted) {
          setState((prev) => ({ ...prev, loading: false, error: true }))
        }
      }
    }

    fetchRegion()

    return () => {
      mounted = false
    }
  }, [])

  return state
}
